// Typed failure surface for the widget.
//
// Every way a session can fail previously collapsed into a bare Error, so an
// embedder could not tell a rejected origin from an exhausted balance from a
// denied microphone, and the end user saw one sentence telling them to open
// devtools. The code is the stable part an integrator branches on; the
// message is addressed to whoever is actually looking at the page.

export type WidgetErrorCode =
  // Microphone acquisition
  | "mic_denied"
  | "mic_not_found"
  | "mic_busy"
  | "mic_unavailable"
  | "insecure_context"
  // Session minting
  | "session_rejected"
  | "session_unavailable"
  | "session_malformed"
  // Transport
  | "connect_failed"
  | "connection_lost"
  | "audio_blocked"
  // Generic
  | "timeout"
  | "aborted"
  | "unknown";

export interface WidgetErrorInit {
  /** Server-supplied error code from the platform envelope, when there was one. */
  serverCode?: string;
  status?: number;
  cause?: unknown;
}

/**
 * Brand that survives the bundle boundary.
 *
 * The drop-in shell and the realtime chunk are built as two independent
 * bundles (see build.mjs), so each inlines its own copy of this module and
 * therefore its own `WidgetError` class. Every error a visitor can hit is
 * minted by the chunk's copy and handled by the shell's, where `instanceof`
 * answers false and would drop the widget to its catch-all. The global symbol
 * registry is shared by both copies, so this is the one identity that crosses.
 */
const WIDGET_ERROR_BRAND = Symbol.for("speechify.agents-widget.error");

/**
 * Whether `err` is a WidgetError, including one minted by another copy of this
 * module. Always prefer this to `instanceof`, which only answers for errors
 * created by the same bundle as the caller.
 */
export function isWidgetError(err: unknown): err is WidgetError {
  return typeof err === "object" && err !== null && (err as Record<symbol, unknown>)[WIDGET_ERROR_BRAND] === true;
}

/**
 * An error the embedder can branch on and the end user can act on.
 *
 * `message` is deliberately end-user copy: it is rendered in the widget on
 * someone else's site, where "check the console" is useless to the person
 * reading it. Diagnostic detail belongs on `serverCode` / `status` / `cause`.
 */
export class WidgetError extends Error {
  readonly code: WidgetErrorCode;
  readonly serverCode?: string;
  readonly status?: number;

  constructor(code: WidgetErrorCode, message: string, init: WidgetErrorInit = {}) {
    super(message, init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = "WidgetError";
    this.code = code;
    this.serverCode = init.serverCode;
    this.status = init.status;
    // Non-enumerable so it stays out of anything that walks the error's own
    // keys on its way into a log line or a host page's error reporter.
    Object.defineProperty(this, WIDGET_ERROR_BRAND, { value: true });
  }
}

/**
 * Classify a getUserMedia rejection.
 *
 * These arrive as DOMExceptions whose `name` is the only reliable
 * discriminator, and they need genuinely different advice: a denied
 * permission is recoverable from the browser's site settings, a busy device
 * usually means another app holds the microphone, and an insecure page can
 * never work at all.
 */
export function classifyMicError(err: unknown): WidgetError {
  const name = err instanceof Error ? err.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return new WidgetError(
        "mic_denied",
        "Microphone access was blocked. Allow it in your browser settings to talk.",
        {
          cause: err,
        },
      );
    case "NotFoundError":
    case "OverconstrainedError":
      return new WidgetError("mic_not_found", "No microphone was found. Connect one and try again.", { cause: err });
    case "NotReadableError":
    case "AbortError":
      return new WidgetError("mic_busy", "Your microphone is in use by another app. Close it and try again.", {
        cause: err,
      });
    default:
      return new WidgetError("mic_unavailable", "Could not access the microphone.", { cause: err });
  }
}

/**
 * Read the platform error envelope off a failed response.
 *
 * Every non-2xx from the API carries `{ error: { code, message } }`, and the
 * widget used to stringify the raw body instead, discarding the one field an
 * integrator can act on.
 */
export async function widgetErrorFromResponse(res: Response): Promise<WidgetError> {
  let serverCode: string | undefined;
  let serverMessage: string | undefined;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    serverCode = body?.error?.code;
    serverMessage = body?.error?.message;
  } catch {
    // A proxy or gateway can answer with HTML; fall through to the
    // status-derived message rather than surfacing a JSON parse error.
  }

  const code =
    res.status === 402 || res.status === 429 || res.status === 403 ? "session_rejected" : "session_unavailable";
  return new WidgetError(code, serverMessage || defaultSessionMessage(res.status), {
    serverCode,
    status: res.status,
  });
}

function defaultSessionMessage(status: number): string {
  if (status === 429) return "Too many requests right now. Try again in a moment.";
  if (status === 402) return "This agent is unavailable right now.";
  if (status === 403 || status === 401) return "This agent is not available on this site.";
  if (status >= 500) return "The service is temporarily unavailable. Try again shortly.";
  return "Could not start the call.";
}

/** End-user copy for a code, used when the widget renders its own error state. */
export function userFacingMessage(err: unknown): string {
  if (isWidgetError(err)) return err.message;
  return "Something went wrong starting the call.";
}

/**
 * A low-cardinality code for telemetry.
 *
 * The platform envelope's own code is preferred when there is one, because it
 * is the specific answer (`spend_cap_exceeded`) where the widget's taxonomy
 * only has the general one (`session_rejected`). Anything that is not a
 * WidgetError collapses to "unknown" rather than being described: an
 * arbitrary error's message is unbounded, frequently contains a URL, and is
 * occasionally something the host page threw.
 */
export function errorCodeOf(err: unknown): string {
  if (!isWidgetError(err)) return "unknown";
  const raw = err.serverCode || err.code;
  const safe = raw.replace(/[^A-Za-z0-9._-]/g, "").slice(0, 64);
  return safe || err.code;
}
